// hooks/useFilters.js
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { fetchSimpleOptions, fetchFilteredOptions } from '../service/api';

const CACHE_TTL = 60000;
let filterOptionsCache = {};

export const useFilters = (userId, apiUrl) => {
  // State
  const [selectedFilters, setSelectedFilters] = useState(() => {
  try {
    const saved = localStorage.getItem('dashboardSelectedFilters');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        date: Array.isArray(parsed.date) ? parsed.date.filter(v => v) : [],
        workspace: Array.isArray(parsed.workspace) ? parsed.workspace.filter(v => v) : [],
        project: Array.isArray(parsed.project) ? parsed.project.filter(v => v) : [],
        job: Array.isArray(parsed.job) ? parsed.job.filter(v => v) : [],
        userName: Array.isArray(parsed.userName) ? parsed.userName.filter(v => v) : [],
        task: Array.isArray(parsed.task) ? parsed.task.filter(v => v) : [],
        priority: Array.isArray(parsed.priority) ? parsed.priority.filter(v => v) : [],
        jobManager: Array.isArray(parsed.jobManager) ? parsed.jobManager.filter(v => v) : [],
      };
    }
  } catch (e) { console.error(e); }
  return { 
    date: [], 
    workspace: [], 
    project: [], 
    job: [], 
    userName: [], 
    task: [],
    priority: [],
    jobManager: []
  };
});

  const [filterOptions, setFilterOptions] = useState({
    date: [], workspace: [], project: [], job: [], userName: [], task: [],priority: [  
    { id: "1", text: "No Priority", displayText: "No Priority", value: 1 },
    { id: "3", text: "High", displayText: "High", value: 3 },
    { id: "4", text: "Medium", displayText: "Medium", value: 4 },
    { id: "5", text: "Completed", displayText: "Completed", value: 5 }
  ],
  jobManager: []
  });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(null);
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
  const [loadingFilterOptions, setLoadingFilterOptions] = useState({
    date: false, workspace: false, project: false, job: false, userName: false, task: false,jobManager: false 
  });
  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState({
    date: false, workspace: false, project: false, job: false, userName: false, task: false, priority: true,  
  jobManager: false
  });

  const saveFiltersDebounceRef = useRef(null);
  const selectedFiltersRef = useRef(selectedFilters);
  useEffect(() => { 
    selectedFiltersRef.current = selectedFilters; 
  }, [selectedFilters]);


const generateFilterCombinations = useCallback((filters) => {
  const combinations = [];
  
  const hasAnyFilters = Object.values(filters).some(arr => arr && arr.length > 0);
  if (!hasAnyFilters) return [];

  const orderedKeys = ['date', 'workspace', 'project', 'job', 'task', 'userName', 'priority', 'jobManager'];
  
  const filterArrays = {};
  orderedKeys.forEach(key => {
    const values = filters[key];
    filterArrays[key] = (values && values.length) ? values : [null];
  });

  const valueArrays = orderedKeys.map(key => filterArrays[key]);
  
  const cartesian = (arrays) => {
    if (!arrays.length) return [];
    return arrays.reduce((acc, curr) => {
      if (!acc.length) return curr.map(item => [item]);
      const result = [];
      acc.forEach(a => {
        curr.forEach(c => {
          result.push([...a, c]);
        });
      });
      return result;
    }, []);
  };

  const allCombinations = cartesian(valueArrays);
  
  allCombinations.forEach(combo => {
    const combination = {};
    orderedKeys.forEach((key, idx) => {
      if (combo[idx] !== null && combo[idx] !== undefined) {
        combination[key] = combo[idx];
      }
    });
    if (Object.keys(combination).length > 0) {
      combinations.push(combination);
    }
  });
  return combinations;
}, []); 

  const transformOptions = useCallback((filterType, items) => {
    switch (filterType) {
      case 'date':
        return items.map(item => {
          const dateStr = item.siteNoteDate;
          try {
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
              const value = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
              return { id: value, text: date.toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }), displayText: date.toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }), rawValue: dateStr };
            }
          } catch(e) {}
          return { id: dateStr.split('T')[0], text: dateStr.split('T')[0], displayText: dateStr.split('T')[0], rawValue: dateStr };
        });
      case 'workspace':
        return items.map(item => ({ id: item.workspaceId, text: item.workspaceName, displayText: item.workspaceName, rawValue: item }));
      case 'project':
        return items.map(item => ({ id: item.projectId, text: item.projectName, displayText: item.projectName, rawValue: item }));
      case 'job':
        return items.map(item => ({ id: item.jobId, text: item.jobName, displayText: item.jobName, rawValue: item }));
      case 'userName':
        return items.map(item => ({ id: item.siteNoteUserId, text: item.siteNoteUserName, displayText: item.siteNoteUserName, rawValue: item }));
      case 'task':
        return items.map(item => ({ id: item.taskId, text: item.taskTitle, displayText: item.taskTitle, rawValue: item }));
      case 'priority':
            const priorityMap = {
              1: "No Priority",
              3: "High",
              4: "Medium",
              5: "Completed"
            };
            return items.map(item => {
              const priorityValue = item.priorityValue || item.value || item;
              const priorityLabel = priorityMap[priorityValue] || `Priority ${priorityValue}`;
              return { 
                id: priorityValue.toString(), 
                text: priorityLabel, 
                displayText: priorityLabel, 
                value: priorityValue,
                rawValue: priorityValue 
              };
            });
          case 'jobManager':
            return items.map(item => ({ 
              id: item.id || item.managerId?.toString(), 
              text: item.text || item.managerName,
              displayText: item.displayText || item.managerName,
              value: item.value || item.managerId,
              rawValue: item 
            }));
          default:
            return [];
        }
  }, []);

  const removeDuplicates = useCallback((items, filterType) => {
    const seen = new Set();
    return items.filter(item => {
      let id;
      switch (filterType) {
        case 'date': id = new Date(item.siteNoteDate).toISOString().split('T')[0]; break;
        case 'workspace': id = item.workspaceId; break;
        case 'project': id = item.projectId; break;
        case 'job': id = item.jobId; break;
        case 'userName': id = item.siteNoteUserId; break;
        case 'task': id = item.taskId; break;
        default: id = JSON.stringify(item);
      }
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, []);

const fetchFilterOptions = useCallback(async (filterType, signal) => {
  
  if (filterType === 'priority') {
    const priorityOptionsList = [
      { id: "1", text: "No Priority", displayText: "No Priority", value: 1 },
      { id: "3", text: "High", displayText: "High", value: 3 },
      { id: "4", text: "Medium", displayText: "Medium", value: 4 },
      { id: "5", text: "Completed", displayText: "Completed", value: 5 }
    ];
    setFilterOptions(prev => ({ ...prev, [filterType]: priorityOptionsList }));
    setFilterOptionsLoaded(prev => ({ ...prev, [filterType]: true }));
    return priorityOptionsList;
  }
  
if (filterType === 'jobManager') {
  if (filterOptions[filterType] && filterOptions[filterType].length > 0) {
    return filterOptions[filterType];
  }
  
  if (loadingFilterOptions[filterType]) {
    return;
  }
  
  if (!userId) {
    console.log('No userId for jobManager fetch');
    return;
  }
  
  setLoadingFilterOptions(prev => ({ ...prev, [filterType]: true }));
  try {
    const url = `${apiUrl}/Filters/GetFilteredJobManager/${userId}`;
    console.log('Fetching job managers from:', url);
    
    const response = await fetch(url, { signal });
    const data = await response.json();
    console.log('Job manager API response:', data);
    
    if (data.managers && Array.isArray(data.managers)) {
      const transformed = data.managers.map(manager => ({
        id: manager.managerId.toString(),
        text: manager.managerName,
        displayText: manager.managerName,
        jobCount: manager.jobCount,
        value: manager.managerId
      }));
      
      console.log('Transformed job managers:', transformed);
      
      setFilterOptions(prev => ({ ...prev, [filterType]: transformed }));
      setFilterOptionsLoaded(prev => ({ ...prev, [filterType]: true }));
      return transformed;
    }
    return [];
  } catch (err) {
    console.error('Error fetching job managers:', err);
    return [];
  } finally {
    setLoadingFilterOptions(prev => ({ ...prev, [filterType]: false }));
  }
}
  
  if (loadingFilterOptions[filterType] || !userId) return;

  const currentSelectedFilters = selectedFiltersRef.current;
  const cacheKey = `${filterType}_${JSON.stringify(currentSelectedFilters)}`;
  const cached = filterOptionsCache[cacheKey];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    setFilterOptions(prev => ({ ...prev, [filterType]: cached.data }));
    setFilterOptionsLoaded(prev => ({ ...prev, [filterType]: true }));
    return cached.data;
  }

  setLoadingFilterOptions(prev => ({ ...prev, [filterType]: true }));
  try {
    const otherFilters = { ...currentSelectedFilters };
    delete otherFilters[filterType];
    const hasFilters = Object.values(otherFilters).some(arr => arr && arr.length > 0);
    let allItems = [];

    if (!hasFilters) {
      allItems = await fetchSimpleOptions(userId, filterType, signal);
    } else {
      const combos = generateFilterCombinations(otherFilters);
      const promises = combos.map(combo => fetchFilteredOptions(userId, filterType, combo, signal));
      const results = await Promise.all(promises);
      results.forEach(items => allItems.push(...items));
      allItems = removeDuplicates(allItems, filterType);
    }

    if (signal?.aborted) return;
    let transformed = transformOptions(filterType, allItems);
    transformed = Array.from(new Map(transformed.map(item => [item.id, item])).values())
      .sort((a,b) => a.text?.localeCompare(b.text) || 0);

    filterOptionsCache[cacheKey] = { data: transformed, timestamp: Date.now() };
    setFilterOptions(prev => ({ ...prev, [filterType]: transformed }));
    setFilterOptionsLoaded(prev => ({ ...prev, [filterType]: true }));
    return transformed;
  } catch (err) {
    if (err.name !== 'AbortError') console.error(`Error fetching ${filterType}:`, err);
    return [];
  } finally {
    if (!signal?.aborted) setLoadingFilterOptions(prev => ({ ...prev, [filterType]: false }));
  }
}, [userId, apiUrl, generateFilterCombinations, transformOptions, removeDuplicates, loadingFilterOptions]);

  const handleFilterCheckboxChange = useCallback((filterType, value) => {
    setSelectedFilters(prev => {
      const current = prev[filterType] || [];
      const newValues = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      const newFilters = { ...prev, [filterType]: newValues };
      if (saveFiltersDebounceRef.current) clearTimeout(saveFiltersDebounceRef.current);
      saveFiltersDebounceRef.current = setTimeout(() => {
        localStorage.setItem('dashboardSelectedFilters', JSON.stringify(newFilters));
      }, 300);
      return newFilters;
    });
  }, []);

  const removeFilter = useCallback((filterType, value) => {
    setSelectedFilters(prev => {
      const newValues = (prev[filterType] || []).filter(v => v !== value);
      const newFilters = { ...prev, [filterType]: newValues };
      localStorage.setItem('dashboardSelectedFilters', JSON.stringify(newFilters));
      return newFilters;
    });
  }, []);

 const clearAllFilters = useCallback(() => {
  const emptyFilters = { 
    date: [], 
    workspace: [], 
    project: [], 
    job: [], 
    userName: [], 
    task: [],
    priority: [],
    jobManager: []
  };
  setSelectedFilters(emptyFilters);
  localStorage.removeItem('dashboardSelectedFilters');
}, []);


const getFilterOptions = useCallback((filterType) => {
  if (filterType === 'priority') {
    const priorityOptionsList = [
      { id: "1", text: "No Priority", displayText: "No Priority", value: 1 },
      { id: "3", text: "High", displayText: "High", value: 3 },
      { id: "4", text: "Medium", displayText: "Medium", value: 4 },
      { id: "5", text: "Completed", displayText: "Completed", value: 5 }
    ];
    
    if (filterSearchTerm) {
      return priorityOptionsList.filter(opt => 
        opt.displayText?.toLowerCase().includes(filterSearchTerm.toLowerCase())
      );
    }
    return priorityOptionsList;
  }
  
  if (filterType === 'jobManager') {
    const jobManagerOptionsList = filterOptions[filterType] || [];
    if (filterSearchTerm) {
      return jobManagerOptionsList.filter(opt => 
        opt.displayText?.toLowerCase().includes(filterSearchTerm.toLowerCase())
      );
    }
    return jobManagerOptionsList;
  }
  
  const options = filterOptions[filterType] || [];
  if (filterSearchTerm) {
    return options.filter(opt => 
      opt.displayText?.toLowerCase().includes(filterSearchTerm.toLowerCase())
    );
  }
  return options;
}, [filterOptions, filterSearchTerm]);

  const getFilterDisplayValue = useCallback((filterType, value, workspaces, projects, jobs) => {
    if (!value) return '';
    const options = filterOptions[filterType] || [];
    const option = options.find(opt => opt.id?.toString() === value?.toString());
    if (option) return option.displayText || option.text || value;
    if (filterType === 'workspace' && workspaces) {
      const ws = workspaces.find(w => w.id?.toString() === value?.toString());
      if (ws) return ws.name || value;
    }
    if (filterType === 'project' && projects) {
      const proj = projects.find(p => p.id?.toString() === value?.toString());
      if (proj) return proj.name || value;
    }
    if (filterType === 'job' && jobs) {
      const job = jobs.find(j => j.id?.toString() === value?.toString());
      if (job) return job.name || value;
    }
    if (filterType === 'date') {
      try {
        const date = new Date(value.split('T')[0]);
        if (!isNaN(date.getTime())) return date.toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' });
      } catch(e) {}
    }
    if (filterType === 'priority') {
    const priorityMap = {
      "1": "No Priority",
      "3": "High",
      "4": "Medium",
      "5": "Completed"
    };
    return priorityMap[value] || value;
  }
    return value;
  }, [filterOptions]);

  const toggleFilterDropdown = useCallback(async (filterType) => {
    if (filterDropdownOpen !== filterType) await fetchFilterOptions(filterType);
    setFilterDropdownOpen(prev => prev === filterType ? null : filterType);
    if (filterDropdownOpen === filterType) setFilterSearchTerm('');
    else setFilterSearchTerm('');
  }, [filterDropdownOpen, fetchFilterOptions]);

  const hasActiveFilters = useMemo(() => Object.values(selectedFilters).some(arr => arr.length > 0), [selectedFilters]);
  const activeFilterCount = useMemo(() => Object.values(selectedFilters).reduce((sum, arr) => sum + arr.length, 0), [selectedFilters]);
  const filterCombinations = useMemo(() => generateFilterCombinations(selectedFilters), [selectedFilters, generateFilterCombinations]);

  useEffect(() => {
    return () => { if (saveFiltersDebounceRef.current) clearTimeout(saveFiltersDebounceRef.current); };
  }, []);

  return {
    selectedFilters,
    setSelectedFilters,
    filterOptions,
    filterDropdownOpen,
    setFilterDropdownOpen,
    filterSearchTerm,
    setFilterSearchTerm,
    loadingFilterOptions,
    filterOptionsLoaded,
    setFilterOptionsLoaded,
    fetchFilterOptions,
    handleFilterCheckboxChange,
    removeFilter,
    clearAllFilters,
    getFilterOptions,
    getFilterDisplayValue,
    toggleFilterDropdown,
    hasActiveFilters,
    activeFilterCount,
    filterCombinations,
    generateFilterCombinations,
  };
};